"use client"

import { useEffect, useState } from "react"
import { useTranslations } from "next-intl"
import { useFormatter } from "@/hooks/use-formatter"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { getTaskHistory, type Task, type TaskUserRef, type TaskHistoryEvent } from "@/lib/tasks-api"

export function TaskHistoryDialog({ task, onClose }: { task: Task; onClose: () => void }) {
  const t = useTranslations("tasks")
  const format = useFormatter()
  const [events, setEvents] = useState<TaskHistoryEvent[]>([])
  const [page, setPage] = useState(1)
  const [lastPage, setLastPage] = useState(1)
  const [attempt, setAttempt] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  useEffect(() => {
    let active = true
    const timer = setTimeout(() => {
    setLoading(true); setError(false)
    getTaskHistory(task.id, page).then((data) => {
      if (active) { setEvents(data.history); setLastPage(data.pagination.last_page) }
    }).catch(() => { if (active) setError(true) }).finally(() => { if (active) setLoading(false) })
    }, 0)
    return () => { active = false; clearTimeout(timer) }
  }, [task.id, page, attempt])
  const valueLabel = (field: string, value: string | number | boolean | TaskUserRef | null) => {
    if (value === null) return "—"
    if (typeof value === "object") return value.full_name
    if (field === "status" && ["open", "done", "cancelled"].includes(String(value))) return t(`status.${value}`)
    if (field === "cancellation_reason" && t.has("blocked." + value)) return t("blocked." + value)
    if (field === "priority" && ["low", "normal", "high"].includes(String(value))) return t(`priority.${value}`)
    if (field === "due_at" && typeof value === "string" && !Number.isNaN(Date.parse(value))) return format.dateTime(new Date(value), { dateStyle: "medium", timeStyle: "short" })
    return String(value)
  }
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader><DialogTitle>{t("history.title")}</DialogTitle><DialogDescription>{task.title}</DialogDescription></DialogHeader>
        {loading ? <p role="status">{t("history.loading")}</p> : error ? (
          <div role="alert"><p>{t("loadFailed")}</p><Button variant="outline" onClick={() => setAttempt((n) => n + 1)}>{t("retry")}</Button></div>
        ) : events.length === 0 ? <p>{t("history.empty")}</p> : (
          <ol className="space-y-4">{events.map((event) => (
            <li key={event.id} className="border-b pb-3 text-sm">
              <p className="font-medium">{t(`history.events.${event.event}`)} · {event.actor?.full_name ?? t("history.system")}</p>
              <p className="text-xs text-muted-foreground">{format.dateTime(new Date(event.created_at), { dateStyle: "medium", timeStyle: "short" })}</p>
              <dl className="mt-2 space-y-1">{Object.entries(event.changes).map(([field, change]) => (
                <div key={field} className="break-words"><dt className="font-medium">{t(`history.fields.${field}`)}</dt><dd className="whitespace-pre-wrap">{valueLabel(field, change.old)} → {valueLabel(field, change.new)}</dd></div>
              ))}</dl>
            </li>
          ))}</ol>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" disabled={loading || page <= 1} onClick={() => setPage((p) => p - 1)}>{t("pagination.prev")}</Button>
          <Button variant="outline" disabled={loading || error || page >= lastPage} onClick={() => setPage((p) => p + 1)}>{t("pagination.next")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
